import { useRef } from 'react';

import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE, STAGGER } from '@/lib/motion-tokens';
import { cn } from '@ibas/ui';

/** The real IBAS vision statement — verbatim, split into word tokens for reveal. */
const VISION_WORDS =
  'At IBAS, our vision is to make education affordable and accessible — so you can learn anywhere, any time, and in any learning method that matches your personality.'.split(
    ' ',
  );

export interface SerenityIntroProps {
  className?: string;
}

/**
 * IBAS "Our vision" statement section — a digital-serenity landing adapted to the
 * brand. A deep navy gradient carries a faint SVG grid and a soft gold radial glow
 * that follows the pointer, while the real vision line appears word by word, each
 * token rising into place like the statement is being composed in front of you.
 *
 * Motion intent: calm, deliberate composure (restrained cinematic) — nothing
 * competes with reading. Under reduced motion the full statement is shown
 * statically and the pointer glow is disabled entirely.
 */
export function SerenityIntro({ className }: SerenityIntroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const wordsRef = useRef<HTMLParagraphElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(sectionRef, { once: true, disabled: reduced });

  useGSAP(
    () => {
      if (reduced || !sectionRef.current) return;

      // Word-by-word appear of the vision line. Driven by the rect-based
      // useInView hook (not gsap.from + ScrollTrigger): a once-fired trigger with
      // a stale/already-passed start position could otherwise strand the whole
      // statement at opacity:0. fromTo carries an explicit visible destination.
      if (wordsRef.current) {
        const words = gsap.utils.toArray<HTMLSpanElement>(
          wordsRef.current.querySelectorAll('[data-word]'),
        );
        if (words.length > 0) {
          if (!inView) {
            // Hold the hidden start state until the section enters view.
            gsap.set(words, { yPercent: 110, opacity: 0 });
          } else {
            gsap.fromTo(
              words,
              { yPercent: 110, opacity: 0 },
              {
                yPercent: 0,
                opacity: 1,
                duration: DURATION.slow,
                ease: GSAP_EASE.out,
                stagger: STAGGER.base,
                overwrite: 'auto',
              },
            );
          }
        }
      }

      // Soft gold radial glow that trails the pointer.
      const glow = glowRef.current;
      const section = sectionRef.current;
      if (!glow) return;

      gsap.set(glow, { xPercent: -50, yPercent: -50, opacity: 0 });
      const moveX = gsap.quickTo(glow, 'x', { duration: DURATION.base, ease: GSAP_EASE.out });
      const moveY = gsap.quickTo(glow, 'y', { duration: DURATION.base, ease: GSAP_EASE.out });

      const handleMove = (event: PointerEvent): void => {
        const rect = section.getBoundingClientRect();
        moveX(event.clientX - rect.left);
        moveY(event.clientY - rect.top);
      };
      const handleEnter = (): void => {
        gsap.to(glow, { opacity: 1, duration: DURATION.base, ease: GSAP_EASE.out });
      };
      const handleLeave = (): void => {
        gsap.to(glow, { opacity: 0, duration: DURATION.base, ease: GSAP_EASE.out });
      };

      document.addEventListener('pointermove', handleMove);
      section.addEventListener('pointerenter', handleEnter);
      section.addEventListener('pointerleave', handleLeave);

      return () => {
        document.removeEventListener('pointermove', handleMove);
        section.removeEventListener('pointerenter', handleEnter);
        section.removeEventListener('pointerleave', handleLeave);
      };
    },
    { scope: sectionRef, dependencies: [reduced, inView] },
  );

  return (
    <section
      ref={sectionRef}
      aria-labelledby="serenity-vision-heading"
      className={cn(
        'relative isolate flex min-h-[100svh] items-center justify-center overflow-hidden',
        'bg-gradient-to-br from-brand-navy via-brand-navy-2 to-brand-navy',
        'px-6 py-12 sm:px-10 sm:py-16 lg:px-16',
        className,
      )}
    >
      {/* Faint SVG grid backdrop */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full text-white/[0.06]"
      >
        <defs>
          <pattern id="serenity-grid" width="64" height="64" patternUnits="userSpaceOnUse">
            <path d="M64 0H0V64" fill="none" stroke="currentColor" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#serenity-grid)" />
      </svg>

      {/* Pointer-following gold radial glow (motion-only) */}
      {!reduced && (
        <div
          ref={glowRef}
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-0 z-0 h-[42rem] w-[42rem] rounded-full opacity-0 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(198,146,20,0.28) 0%, rgba(221,178,70,0.10) 40%, transparent 70%)',
          }}
        />
      )}

      {/* Corner detail marks */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-6 left-6 h-10 w-10 border-t border-l border-brand-gold/40 sm:top-10 sm:left-10"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-6 right-6 h-10 w-10 border-t border-r border-brand-gold/40 sm:top-10 sm:right-10"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-6 left-6 h-10 w-10 border-b border-l border-brand-gold/40 sm:bottom-10 sm:left-10"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-6 bottom-6 h-10 w-10 border-r border-b border-brand-gold/40 sm:right-10 sm:bottom-10"
      />

      <div className="relative z-10 mx-auto max-w-4xl text-center">
        <p className="font-label text-xs tracking-[0.35em] text-brand-gold uppercase sm:text-sm">
          Our vision
        </p>

        <h2 id="serenity-vision-heading" className="sr-only">
          Our vision
        </h2>

        <p
          ref={wordsRef}
          className="mt-8 font-display text-3xl leading-[1.35] font-light text-white sm:text-4xl md:text-5xl md:leading-[1.3]"
        >
          {VISION_WORDS.map((word, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`${word}-${index}`}
              className="mr-[0.28em] inline-block overflow-hidden pb-[0.06em] align-bottom"
            >
              <span data-word className="inline-block">
                {word}
              </span>
            </span>
          ))}
        </p>

        <p className="mt-10 font-sans text-base text-white/70">Study Your Way. Connect Globally.</p>
      </div>
    </section>
  );
}
